// src/components/CountryBundlesGrid.tsx
"use client";

import React, { useMemo, useCallback, useEffect, useState } from "react";
import { BundleWithCountries, Country } from "@/types/types";
import { formatCurrency, convertPrice, isStripeCurrency } from "@/utils/currencyUtils";
import { useTranslations } from "@/context/TranslationsContext";
import BundleCard from "./BundleCard";
import { getBundleRating, getBundleReviewCount } from "@/utils/bundleHelpers";
import { getCountryName } from "@/lib/countryTranslations";

interface CountryBundlesGridProps {
  country: Country & { networkBrands: string[] };
  bundles: (BundleWithCountries & { localizedName: string })[];
  allCountries: { iso: string; name: string }[];
  currencyCode: string;
  exchangeRate: number;
  onBuy: (bundle: BundleWithCountries & { localizedName: string }) => void;
}

export default function CountryBundlesGrid({
  country,
  bundles,
  allCountries,
  currencyCode,
  exchangeRate,
  onBuy,
}: CountryBundlesGridProps) {
  const { t, language, direction } = useTranslations();
  const isRtl = direction === "rtl";

  // Compute networks fallback if none provided on country
  const networksFallback = useMemo(() => {
    if (country.networkBrands.length > 0) {
      return country.networkBrands;
    }
    const set = new Set<string>();
    bundles.forEach((b) =>
      b.countries.forEach((c) =>
        (c.networks || []).forEach((n) => set.add(n.brand))
      )
    );
    return Array.from(set).sort();
  }, [country.networkBrands, bundles]);

  // Helper to map ISO → localized country name
  const countryLabel = useCallback(
    (iso: string) => {
      const localized = getCountryName(iso, language);
      if (localized) return localized;
      const found = allCountries.find((c) => c.iso === iso)?.name;
      return found || iso.toUpperCase();
    },
    [allCountries, language]
  );

  // Build full bundle names map if not already provided
  const [bundleNames, setBundleNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const entries = bundles.map((b) => [
      b.id.toString(),
      b.localizedName,
    ] as const);
    setBundleNames(Object.fromEntries(entries));
  }, [bundles]);

  // Render the grid of BundleCard components
  const renderedBundles = useMemo(
    () =>
      bundles.map((bundle) => {
        const idStr = bundle.id.toString();
        const name = bundleNames[idStr] || "";
        const roaming = bundle.countries.map((c) => countryLabel(c.iso));

        // Convert + format price
        const convertedPrice = convertPrice(
          bundle.price ?? 0,
          exchangeRate,
          currencyCode
        );
        const price = formatCurrency(convertedPrice, currencyCode);

        const rating = getBundleRating(bundle.id);
        const reviews = getBundleReviewCount(bundle.id);

        return (
          <BundleCard
            key={idStr}
            bundle={bundle}
            name={name}
            roaming={roaming}
            price={price}
            rating={rating}
            reviews={reviews}
            networks={networksFallback}
            onBuy={() => onBuy(bundle)}
            t={t}
            isRtl={isRtl}
          />
        );
      }),
    [
      bundles,
      bundleNames,
      countryLabel,
      networksFallback,
      t,
      isRtl,
      currencyCode,
      exchangeRate,
      onBuy,
    ]
  );

  return (
    <section className="mt-8 px-4 md:px-8">
      {bundles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
          {renderedBundles}
        </div>
      ) : (
        <p className="text-center text-gray-500 text-lg">
          {t("countryPageClient.noBundles")}
        </p>
      )}
    </section>
  );
}
